import * as R from "ramda";

import { featuresOfTypeForPoint, getPoint } from "services/MVT";
import { compact, isPresent } from "utils/func";

const UNUSED_ZONE_ATTRIBUTES = [
  "conditional_clearances_count",
  "keywords",
  "name",
  "overlay",
  "permitted_clearances_count",
  "prohibited_clearances_count",
  "searchable_text",
  "tenant_id",
  "tenant_slug",
  "total_clearances_count",
  "zone_group_id",
];

export const selectZoningData = ({
  lat,
  lng,
  x,
  y,
  z,
  useCodeSlug,
  answers,
  clearances,
  zonesTile,
  parcelsTile,
  rulesTile,
}) => {
  const point = getPoint(lat, lng);
  const zones = featuresOfTypeForPoint("zones", zonesTile, point, x, y, z);
  const parcels = featuresOfTypeForPoint("parcels", parcelsTile, point, x, y, z);

  let zonesAnswer;

  if (useCodeSlug) {
    const rules = featuresOfTypeForPoint("rules", rulesTile, point, x, y, z);

    const radiusRuleIDs = R.map(R.prop("id"), rules);
    const ruleIDs = getApplicableYesNoRuleIDs(answers, clearances);

    const zonesWithPermission = getPermissionsForZones(zones, ruleIDs, radiusRuleIDs, clearances);

    zonesAnswer = R.pipe(
      zonesToAnswer,
      R.map(
        R.over(
          R.lensProp("rule"),
          R.when(isPresent, R.omit(["prompt", "clearance_description", "evaluator", "value"])),
        ),
      ),
    )(zonesWithPermission);
  } else {
    zonesAnswer = zonesToAnswer(zones);
  }

  return {
    zones: zonesAnswer,
    parcels: reshapeParcels(parcels),
  };
};

const zonesToAnswer = R.map((zone) => {
  zone.display_name = zone.name;
  zone.is_overlay = zone.overlay;
  return R.omit(UNUSED_ZONE_ATTRIBUTES, zone);
});

const reshapeParcels = R.pipe(
  R.filter(R.prop("parcel_number")),
  R.map((p) => ({ key: "Parcel Number", value: p.parcel_number })),
);

export const getApplicableYesNoRuleIDs = (answers, clearances) => {
  const rules = getAllRules(clearances);
  return R.pipe(
    R.filter((r) => answers[r.slug.replace(/-/g, "_")] === r.value),
    R.map(R.prop("id")),
  )(rules);
};

export const getAllRules = (clearances) =>
  R.pipe(
    R.values,
    R.flatten,
    R.map(R.prop("rule")),
    R.filter(
      R.allPass([Boolean, R.propEq("Yes/No", "rule_type"), R.propEq("equals", "evaluator")]),
    ),
    R.uniqBy(R.prop("id")),
  )(clearances);

const getPermissionsForZones = (zones, ruleIDs, radiusRuleIDs, clearances) => {
  const applicableRuleIDs = R.concat(ruleIDs, radiusRuleIDs);

  return R.pipe(
    R.map((zone) =>
      R.mergeRight(zone, activePermissionAndRuleForZoneID(clearances[zone.id], applicableRuleIDs)),
    ),
    R.sortWith([
      R.descend(R.has("permission")),
      R.descend(R.prop("is_overlay")),
      R.descend(R.prop("overlay")),
      R.descend(R.prop("rule")),
      R.descend(R.path(["permission", "priority"])),
    ]),
  )(zones);
};

export const activePermissionAndRuleForZoneID = (zoneClearances, applicableRuleIDs) => {
  if (!zoneClearances) return {}; // Overlays might not have clearance
  const clearance = R.find(
    (c) => R.includes(R.path(["rule", "id"], c), applicableRuleIDs) || !c.rule,
    zoneClearances,
  );
  if (!clearance) return {}; // Overlays might not have applicable clearance

  return R.over(
    R.lensPath(["permission", "category"]),
    (s) => s.toLowerCase().replace("permission::", ""),
    clearance,
  );
};

export const getZonePermissionCategory = (zone) => R.path(["permission", "category"], zone);
const getZones = R.propOr([], "zones");
export const getParcelIDs = R.pipe(R.propOr([], "parcels"), R.pluck("value"), compact);

export const getZonesWithDevelopmentStandards = R.pipe(
  getZones,
  R.filter(R.prop("development_standards_url")),
);
export const getEffectiveZone = R.pipe(getZones, R.head);
export const getOverlays = R.pipe(getZones, R.filter(R.prop("is_overlay")));
export const getBaseZone = R.pipe(getZones, R.find(R.complement(R.prop("is_overlay"))));

export const getPermission = R.pipe(getEffectiveZone, R.defaultTo({}), R.prop("permission"));
const getZoningPermissionCategory = R.pipe(
  getEffectiveZone,
  R.defaultTo({}),
  getZonePermissionCategory,
);
export const getIsProhibited = R.pipe(getZoningPermissionCategory, R.equals("prohibited"));

const getZoneZoningDistrict = (zone) =>
  zone && zone.code ? `${zone.display_name} (${zone.code})` : undefined;
export const getZoningDistrict = R.pipe(getBaseZone, getZoneZoningDistrict);

export const getIsZoningPresent = R.converge(
  R.or,
  R.map(R.propSatisfies(isPresent, R.__), ["zones", "parcels"]),
);
